import { pluralize } from 'inflection';
import _ from 'lodash';
import React from 'react';
import LoadingIndicator from '../../components/LoadingIndicator';
import type { IntegrationSettingsSearch } from '../../graphql/generated/graphql';
import type { IntegrationPlatformInfo } from '../../integrations/IntegrationPlatformInfo';
import IntegrationSelectSetting, {
  EnableOption,
  SETTING_OPTIONS,
} from './IntegrationSelectSetting';
import useTeamSearchSources from './useTeamSearchSources';
import { SELECT_OPTIONS } from './IntegrationSelectSettingItem';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';

type ItemMap = Record<
  string,
  {
    select: typeof SELECT_OPTIONS[keyof typeof SELECT_OPTIONS];
    ids: Set<string>;
  }
>;
const generateSelectedItems = ({
  items = [],
  selectedIds,
}: {
  items: { id: string; subItems?: { id: string }[] }[];
  selectedIds: string[];
}) => {
  const selectedItems: ItemMap = {};
  const selectedIdsSet = new Set(selectedIds);
  items.forEach(({ id, subItems = [] }) => {
    if (selectedIdsSet.has(id)) {
      selectedItems[id] = {
        select: SELECT_OPTIONS.all,
        ids: new Set(),
      };
    }
    subItems.forEach((subItem) => {
      if (selectedIdsSet.has(subItem.id)) {
        selectedItems[id] = selectedItems[id] || {
          select: SELECT_OPTIONS.custom,
          ids: new Set(),
        };
        selectedItems[id]?.ids.add(subItem.id);
      }
    });
  });
  return selectedItems;
};

const getFormInitialValues = ({
  initialValues,
  items: itemsOrItemsLoader,
}: {
  initialValues: {
    search: {
      enable: IntegrationSettingsSearch | EnableOption;
      selectedIds: string[];
    };
  };
  items?: { id: string; name: string }[];
}): {
  search:
    | {
        enable: IntegrationSettingsSearch | EnableOption;
        selectedIds: string[];
        selectedItems?: never;
      }
    | {
        enable: IntegrationSettingsSearch | EnableOption;
        selectedIds?: never;
        selectedItems: ItemMap;
      };
} =>
  _.mapValues(initialValues, (value, setting) =>
    SETTING_OPTIONS.includes(setting)
      ? {
          enable: value.enable,
          selectedItems:
            !itemsOrItemsLoader || typeof itemsOrItemsLoader === 'function'
              ? {}
              : generateSelectedItems({
                  items: itemsOrItemsLoader,
                  selectedIds: value.selectedIds,
                }),
        }
      : value,
  );

const getSelectedIds = (itemMap: ItemMap = {}) =>
  Object.entries(itemMap).flatMap(([id, { ids: subIds, select }]) =>
    select === 'all' ? [id] : [...subIds],
  );

const INTEGRATION_FRAGMENT = gql(`
  fragment TeamSearchTab_integration on Integration {
    id
    platform
    settings {
      enableSearch
      enabledSearchSources
    }
    ...useTeamSearchSources_integration
  }
`);

interface TeamSearchTabProps {
  integration: DocumentType<typeof INTEGRATION_FRAGMENT>;
  platformInfo: IntegrationPlatformInfo;
}

const TeamSearchTab = ({ integration, platformInfo }: TeamSearchTabProps) => {
  const { data: { sources } = {}, error } = useTeamSearchSources(integration);

  if (!sources && !error) {
    return <LoadingIndicator />;
  }
  const { mention, name } = platformInfo;

  if (error) {
    return (
      <>{`We encountered an error when retrieving ${pluralize(
        mention?.parentName ?? '',
      )}. Please re-connect ${name} and then retry.`}</>
    );
  }

  return sources ? (
    <IntegrationSelectSetting
      name="search"
      items={sources}
      itemIcon={mention?.parentIcon}
      itemTerm={mention?.parentName ?? ''}
      enableOptions={mention?.searchEnabledOptions ?? []}
    />
  ) : (
    <LoadingIndicator />
  );
};

TeamSearchTab.getFormValues = (
  { settings }: DocumentType<typeof INTEGRATION_FRAGMENT>,
  { teamSearchSources }: { teamSearchSources?: { id: string; name: string }[] },
) =>
  getFormInitialValues({
    initialValues: {
      search: {
        enable: settings?.enableSearch || EnableOption.disabled,
        selectedIds: settings?.enabledSearchSources || [],
      },
    },
    items: teamSearchSources,
  });

TeamSearchTab.getNewSettings = ({
  search: { selectedItems, enable },
}: ReturnType<typeof TeamSearchTab.getFormValues>) => {
  const enabledSearchSources = getSelectedIds(selectedItems);
  const enableSearch =
    enable === EnableOption.custom && enabledSearchSources.length === 0
      ? EnableOption.disabled
      : enable;

  return { enableSearch, enabledSearchSources };
};

export default TeamSearchTab;
